/**
 * artifactDownload — save the artifact ALREADY IN THE CLIENT as a file, fully
 * client-side. When you click download the box is already rendered/hydrated, so
 * the body (html / text / table / base64 file) is in memory — we just wrap it in
 * a blob and save it. NO refetch, and NEVER the presigned S3 url (the browser→S3
 * request is CORS-blocked in the app).
 */
import type { RenderFile } from "./contract";

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

export const MIME_BY_SUFFIX: Record<string, string> = {
  html: "text/html", svg: "image/svg+xml", pdf: "application/pdf",
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
  md: "text/markdown", txt: "text/plain", csv: "text/csv",
  json: "application/json", js: "text/javascript", ts: "text/plain", py: "text/x-python",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export const mimeForSuffix = (suffix: string): string =>
  MIME_BY_SUFFIX[suffix.toLowerCase()] || "application/octet-stream";

export const sanitizeFilename = (name: string): string =>
  String(name || "").replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/^_+|_+$/g, "") || "artifact";

export const base64ToBytes = (b64: string): Uint8Array => {
  // tolerate a data: url prefix (data:application/pdf;base64,XXXX)
  const clean = b64.includes(",") && /^data:/i.test(b64) ? b64.slice(b64.indexOf(",") + 1) : b64;
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

const payloadOf = (artifact: Record<string, unknown>, render?: RenderFile): Record<string, unknown> => {
  const input = isObject(artifact.input) ? artifact.input : {};
  const output = isObject(artifact.output) ? artifact.output : {};
  const rp = isObject(render?.payload) ? render!.payload as Record<string, unknown> : {};
  return { ...rp, ...output, ...input };
};

// An interactive artifact downloads as a COMPLETE html document so it OPENS and
// RUNS in a browser (stays interactive — never a flattened/stale snapshot). A
// recomposed template is already a full design-tokened doc; a bare fragment is
// wrapped so it still opens standalone.
const asStandaloneHtml = (html: string): string => {
  const head = html.trimStart().slice(0, 64).toLowerCase();
  if (head.startsWith("<!doctype") || head.startsWith("<html")) return html;
  return `<!doctype html>\n<html lang="en"><head><meta charset="utf-8">`
    + `<meta name="viewport" content="width=device-width, initial-scale=1"></head>`
    + `<body>${html}</body></html>`;
};

const rowsToCsv = (rows: Record<string, unknown>[]): string => {
  if (!rows.length) return "";
  const cols = Array.from(rows.reduce<Set<string>>((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  const esc = (v: unknown) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

/** Build a downloadable file from a HYDRATED artifact. Returns null when the
 * artifact carries no saveable body (caller can fall back to fetching bytes). */
export const artifactToFile = (artifactInput: unknown, render?: RenderFile): { blob: Blob; filename: string } | null => {
  const artifact = isObject(artifactInput) ? artifactInput : {};
  const machine = isObject(artifact.machine) ? artifact.machine : {};
  const suffix = String(artifact.suffix || render?.suffix || "").toLowerCase();
  const payload = payloadOf(artifact, render);
  const base = sanitizeFilename(String(machine.label || machine.name || "artifact"));
  const file = (data: string | ArrayBuffer, ext: string, mime?: string) => ({
    blob: new Blob([data], { type: mime || mimeForSuffix(ext) }),
    filename: `${base}.${ext || "txt"}`,
  });

  // HTML — the recomposed interactive body (welcome / surface / report-as-html).
  const html = payload.html
    ?? (typeof artifact.input === "string" ? artifact.input : undefined)
    ?? (typeof payload.body === "string" ? payload.body : undefined);
  if ((suffix === "html" || artifact.position === "interactive") && typeof html === "string" && html) {
    return file(asStandaloneHtml(html), "html");
  }
  // SVG (inline vector body)
  if (suffix === "svg" && typeof payload.svg === "string" && payload.svg) return file(payload.svg, "svg");
  // binary by suffix carried as base64/data-url (pdf, png, jpg, …)
  const binaryKeys = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "content", "file", "bytes", "base64"];
  for (const key of binaryKeys) {
    const v = payload[key];
    if (typeof v === "string" && v && /^[A-Za-z0-9+/=]+$|^data:/.test(v.slice(0, 32))) {
      const ext = ["content", "file", "bytes", "base64"].includes(key) ? (suffix || "bin") : key;
      try { return file(base64ToBytes(v).buffer as ArrayBuffer, ext); } catch { /* not base64 — keep looking */ }
    }
  }
  // tables / entity → CSV
  const rows = (Array.isArray(payload.rows) ? payload.rows : Array.isArray(payload.items) ? payload.items : [])
    .filter(isObject) as Record<string, unknown>[];
  if (rows.length && (suffix === "csv" || suffix === "xlsx" || !suffix)) {
    return file(rowsToCsv(rows), "csv", MIME_BY_SUFFIX.csv);
  }
  // text family (markdown / txt / code)
  const text = payload.text ?? payload.markdown ?? payload.content ?? payload.body;
  if (typeof text === "string" && text) return file(text, suffix || "txt");
  // structured fallback → JSON
  if (artifact.input != null || rows.length) {
    return file(JSON.stringify(artifact.input ?? rows, null, 2), "json", MIME_BY_SUFFIX.json);
  }
  return null;
};

/** Save a blob as a file, the standard anchor-click way (no new tab, no S3). */
export const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
};
